import React from 'react';

const UpcomingSessionsList = React.memo(function UpcomingSessionsList({
  sessions = [],
  limit = 5,
  isArabic,
  onViewAll,
  onOpenCalendar
}) {
  const translations = {
    title: { en: 'Upcoming Sessions', ar: 'الجلسات القادمة' },
    viewAll: { en: 'View All', ar: 'عرض الكل' },
    openCalendar: { en: 'Open Calendar', ar: 'فتح التقويم' },
    with: { en: 'with', ar: 'مع' },
    noSessions: { en: 'No upcoming sessions', ar: 'لا توجد جلسات قادمة' }
  };

  const displayedSessions = sessions.slice(0, limit);

  // Format date to "OCT 24" format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    return { month, day };
  };

  // Format time to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? (isArabic ? 'م' : 'PM') : (isArabic ? 'ص' : 'AM');
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Truncate subject name
  const truncateSubject = (subject, maxLength = 20) => {
    if (!subject) return '';
    return subject.length > maxLength ? subject.substring(0, maxLength) + '...' : subject;
  };

  return (
    <div
      className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 flex flex-col h-full"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-bold text-gray-900">
          {translations.title[isArabic ? 'ar' : 'en']}
        </h3>
        {onViewAll && sessions.length > limit && (
          <button
            onClick={onViewAll}
            className="text-brand-orange text-sm font-medium hover:underline"
          >
            {translations.viewAll[isArabic ? 'ar' : 'en']}
          </button>
        )}
      </div>

      {/* Sessions List */}
      <div className="flex-1 space-y-4 mb-4">
        {displayedSessions.length > 0 ? (
          displayedSessions.map((session, index) => {
            const { month, day } = formatDate(session.date);
            return (
              <div
                key={session.id || index}
                className="flex gap-3 items-start p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {/* Date Badge */}
                <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
                  <span className="text-xs font-semibold text-brand-orange">
                    {month}
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {day}
                  </span>
                </div>

                {/* Session Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                    {truncateSubject(session.subject || session.request_type)}
                  </h4>
                  <p className="text-xs text-gray-600 mb-1">
                    {translations.with[isArabic ? 'ar' : 'en']} {session.student_name}
                  </p>
                  <p className="text-xs text-brand-orange font-medium">
                    {formatTime(session.time_from)}
                  </p>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-gray-400 text-sm">
              {translations.noSessions[isArabic ? 'ar' : 'en']}
            </p>
          </div>
        )}
      </div>

      {/* Open Calendar Button */}
      {onOpenCalendar && (
        <button
          onClick={onOpenCalendar}
          className="w-full py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm rounded-lg transition-colors"
        >
          {translations.openCalendar[isArabic ? 'ar' : 'en']}
        </button>
      )}
    </div>
  );
});

export default UpcomingSessionsList;
